import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isEmbroideryTrace } from "../../config/appVariant";
import { IonButton, IonIcon } from '@ionic/react';
import { 
  closeOutline, 
  arrowForwardOutline, 
  arrowBackOutline,
  checkmarkOutline,
  sparklesOutline,
  cloudUploadOutline,
  downloadOutline,
  brushOutline,
  playOutline,
  pauseOutline
} from 'ionicons/icons';
import { useOnboarding } from './OnboardingProvider';
import './OnboardingOverlay.css';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to SVG Trace! 🎨',
    description: 'Ready to transform your images into professional SVGs? Let\'s learn the magic together!',
    position: 'center',
    showSkip: true,
    icon: sparklesOutline,
    action: null,
    celebrationEmoji: '✨',
    helpText: 'This quick tutorial will have you creating SVGs like a pro in under 2 minutes!'
  },
  {
    id: 'upload-intro',
    title: 'Step 1: Upload Your Image 📸',
    description: 'Click here to upload any PNG, JPG, or WEBP image. We\'ll work our magic to convert it!',
    targetSelector: '.sidebar-item[data-id="create"], .upload-section, [data-testid="upload-area"]',
    position: 'right',
    icon: cloudUploadOutline,
    action: 'highlight',
    celebrationEmoji: '🖼️',
    helpText: 'Pro tip: High contrast images with clear shapes work best!',
    interactive: true,
    nextOnAction: false
  },
  {
    id: 'ai-generation',
    title: 'Or Try AI Magic! 🪄',
    description: 'No image? No problem! Describe what you want and our AI will create it for you.',
    targetSelector: '[data-action="generate"], .generate-button, .ai-generate-btn',
    position: 'left',
    icon: sparklesOutline,
    action: 'highlight',
    celebrationEmoji: '🤖',
    helpText: 'Try prompts like "cartoon cat" or "minimalist logo"',
    interactive: true,
    nextOnAction: false
  },
  {
    id: 'conversion-process',
    title: 'Watch the Magic Happen! ⚡',
    description: isEmbroideryTrace ? 'Upload your artwork to start preparing your embroidery design.' : 'Once you upload or generate an image, our AI automatically converts it to a crisp, scalable SVG.',
    position: 'center',
    icon: brushOutline,
    action: null,
    celebrationEmoji: '🎯',
    helpText: 'This usually takes just a few seconds - perfect time for a coffee sip!'
  },
  {
    id: 'download-result',
    title: 'Download Your Creation! 💾',
    description: 'Your SVG is ready! Click here to download and use it in any design project.',
    targetSelector: '.download-button, [data-action="download"], [data-onboarding-target="download"]',
    position: 'top',
    icon: downloadOutline,
    action: 'highlight',
    celebrationEmoji: '🎉',
    helpText: 'SVGs scale perfectly and work everywhere - websites, print, you name it!',
    interactive: true,
    nextOnAction: false
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🏆',
    description: 'Congratulations! You\'ve mastered SVG Trace. Ready to create something amazing?',
    position: 'center',
    showComplete: true,
    icon: checkmarkOutline,
    action: null,
    celebrationEmoji: '🎊',
    helpText: 'Pro tip: Use the floating help button anytime you need a refresher!'
  }
].filter(step => !isEmbroideryTrace || step.id !== 'ai-generation');

const OnboardingOverlay = ({ isVisible, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [pulseCount, setPulseCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const animationRef = useRef();
  const celebrationTimeoutRef = useRef();
  const { updateProgress, markWelcomeSeen } = useOnboarding();

  const step = ONBOARDING_STEPS[currentStep];

  // Smooth scroll to target element
  const scrollToElement = useCallback((element) => {
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
    
    if (!isInView) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
  }, []);

  // Calculate tooltip position with smart positioning
  const calculateTooltipPosition = useCallback((targetElement, position) => {
    if (!targetElement) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 400;
    const tooltipHeight = 280;
    const padding = 20;

    let top, left, transform = '';

    switch (position) {
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + padding;
        transform = 'translateY(-50%)';
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - tooltipWidth - padding;
        transform = 'translateY(-50%)';
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
        break;
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
        break;
      default:
        top = window.innerHeight / 2;
        left = window.innerWidth / 2;
        transform = 'translate(-50%, -50%)';
    }

    // Smart viewport adjustment
    if (left < padding) {
      left = padding;
      transform = transform.replace('translateX(-50%)', '');
    }
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
      transform = transform.replace('translateX(-50%)', '');
    }
    if (top < padding) {
      top = padding;
      transform = transform.replace('translateY(-50%)', '');
    }
    if (top + tooltipHeight > window.innerHeight - padding) {
      top = window.innerHeight - tooltipHeight - padding;
      transform = transform.replace('translateY(-50%)', '');
    }

    return { top, left, transform };
  }, []);

  // Update highlighted element and tooltip position with animation
  useEffect(() => {
    if (!isVisible || !step) return;

    setIsAnimating(true);
    
    const updatePositions = () => {
      if (step.targetSelector) {
        const targetElement = document.querySelector(step.targetSelector);
        if (targetElement) {
          setHighlightedElement(targetElement);
          scrollToElement(targetElement);
          const position = calculateTooltipPosition(targetElement, step.position);
          setTooltipPosition(position);
        } else {
          // Fallback to center if target not found
          setHighlightedElement(null);
          setTooltipPosition({
            top: window.innerHeight / 2,
            left: window.innerWidth / 2,
            transform: 'translate(-50%, -50%)'
          });
        }
      } else {
        setHighlightedElement(null);
        setTooltipPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
          transform: 'translate(-50%, -50%)'
        });
      }
      
      setTimeout(() => setIsAnimating(false), 300);
    };

    // Small delay for smooth transitions
    setTimeout(updatePositions, 100);
  }, [currentStep, isVisible, step, calculateTooltipPosition, scrollToElement]);

  // Enhanced highlight effect with pulsing animation
  useEffect(() => {
    if (highlightedElement && step.action === 'highlight' && isPlaying) {
      highlightedElement.classList.add('onboarding-highlight');
      highlightedElement.style.zIndex = '10001';
      
      // Pulsing animation
      const startPulse = () => {
        if (pulseCount < 3) {
          highlightedElement.classList.add('onboarding-pulse');
          setTimeout(() => {
            if (highlightedElement) {
              highlightedElement.classList.remove('onboarding-pulse');
            }
          }, 1000);
          setPulseCount(prev => prev + 1);
          animationRef.current = setTimeout(startPulse, 2000);
        }
      };
      
      startPulse();
      
      return () => {
        if (highlightedElement) {
          highlightedElement.classList.remove('onboarding-highlight', 'onboarding-pulse');
          highlightedElement.style.zIndex = '';
        }
        if (animationRef.current) {
          clearTimeout(animationRef.current);
        }
      };
    }
  }, [highlightedElement, step, pulseCount, isPlaying]);

  // Celebration effect
  const triggerCelebration = useCallback(() => {
    setShowCelebration(true);
    celebrationTimeoutRef.current = setTimeout(() => {
      setShowCelebration(false);
    }, 2000);
  }, []);

  const handleNext = () => {
    // Update progress tracking
    updateProgress(currentStep, ONBOARDING_STEPS.length);
    
    // Mark welcome as seen on first step
    if (currentStep === 0) {
      markWelcomeSeen();
    }
    
    // Celebration for key milestones
    if (currentStep === 0 || currentStep === 3) {
      triggerCelebration();
    }
    
    setPulseCount(0);
    setHasUserInteracted(true);
    
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setPulseCount(0);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    triggerCelebration();
    setTimeout(() => {
      localStorage.setItem('svgtrace_onboarding_completed', 'true');
      onComplete();
    }, 1000);
  };

  const handleSkip = () => {
    localStorage.setItem('svgtrace_onboarding_completed', 'true');
    onSkip();
  };

  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div className="onboarding-overlay">
      {/* Animated background */}
      <div className="onboarding-backdrop">
        <div className="onboarding-particles">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`} />
          ))}
        </div>
      </div>
      
      {/* Enhanced spotlight effect */}
      {highlightedElement && (
        <>
          <div 
            className="onboarding-spotlight"
            style={{
              top: highlightedElement.offsetTop - 15,
              left: highlightedElement.offsetLeft - 15,
              width: highlightedElement.offsetWidth + 30,
              height: highlightedElement.offsetHeight + 30,
            }}
          />
          <div 
            className="onboarding-glow"
            style={{
              top: highlightedElement.offsetTop - 25,
              left: highlightedElement.offsetLeft - 25,
              width: highlightedElement.offsetWidth + 50,
              height: highlightedElement.offsetHeight + 50,
            }}
          />
        </>
      )}

      {/* Main tooltip card */}
      <div 
        className={`onboarding-tooltip ${isAnimating ? 'animating' : ''} ${step.id}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
        }}
      >
        {/* Character mascot */}
        <div className="onboarding-character">
          <div className="character-avatar">
            <div className="character-face">
              <div className="character-eyes">
                <div className="eye left"></div>
                <div className="eye right"></div>
              </div>
              <div className="character-mouth"></div>
            </div>
          </div>
          <div className="character-speech-bubble">
            <span className="celebration-emoji">{step.celebrationEmoji}</span>
          </div>
        </div>

        {/* Header */}
        <div className="onboarding-tooltip-header">
          <div className="onboarding-step-info">
            <div className="step-icon-container">
              <IonIcon icon={step.icon} className="onboarding-step-icon" />
            </div>
            <div className="step-meta">
              <span className="onboarding-step-counter">
                Step {currentStep + 1} of {ONBOARDING_STEPS.length}
              </span>
              <div className="step-progress-bar">
                <div 
                  className="step-progress-fill"
                  style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="header-controls">
            {step.interactive && (
              <IonButton 
                fill="clear" 
                size="small" 
                onClick={togglePlayback}
                className="playback-control"
                title={isPlaying ? 'Pause animations' : 'Resume animations'}
              >
                <IonIcon icon={isPlaying ? pauseOutline : playOutline} />
              </IonButton>
            )}
            {step.showSkip && (
              <IonButton 
                fill="clear" 
                size="small" 
                onClick={handleSkip}
                className="onboarding-skip-btn"
              >
                Skip Tour
              </IonButton>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="onboarding-tooltip-content">
          <h3 className="onboarding-tooltip-title">{step.title}</h3>
          <p className="onboarding-tooltip-description">{step.description}</p>
          {step.helpText && (
            <div className="onboarding-help-text">
              <span className="help-icon">💡</span>
              {step.helpText}
            </div>
          )}
        </div>

        {/* Interactive elements */}
        {step.interactive && highlightedElement && (
          <div className="interactive-hint">
            <div className="hint-arrow"></div>
            <span>Try clicking the highlighted area!</span>
          </div>
        )}

        {/* Actions */}
        <div className="onboarding-tooltip-actions">
          {currentStep > 0 && (
            <IonButton 
              fill="clear" 
              onClick={handlePrevious}
              className="onboarding-btn-secondary"
            >
              <IonIcon icon={arrowBackOutline} slot="start" />
              Back
            </IonButton>
          )}
          
          <div className="onboarding-progress-dots">
            {ONBOARDING_STEPS.map((_, index) => (
              <div 
                key={index}
                className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>

          {step.showComplete ? (
            <IonButton 
              onClick={handleComplete}
              className="onboarding-btn-primary celebration"
            >
              <IonIcon icon={checkmarkOutline} slot="start" />
              Let's Go!
            </IonButton>
          ) : (
            <IonButton 
              onClick={handleNext}
              className="onboarding-btn-primary"
            >
              {currentStep === ONBOARDING_STEPS.length - 1 ? 'Finish' : 'Continue'}
              <IonIcon icon={arrowForwardOutline} slot="end" />
            </IonButton>
          )}
        </div>
      </div>

      {/* Celebration overlay */}
      {showCelebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <div className="celebration-emoji-burst">
              {['🎉', '✨', '🎊', '🌟', '💫'].map((emoji, i) => (
                <div key={i} className={`celebration-emoji celebration-emoji-${i}`}>
                  {emoji}
                </div>
              ))}
            </div>
            <div className="celebration-text">Great job!</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingOverlay;
